import {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import NextLink from 'next/link';
import {object} from 'yup';

import {Button, Form, Link, Text} from '#component/base';
import {Checkbox} from '#component/base';
import {ModalRoot, ModalTrigger} from '#component/base/Modal';
import {ContactUsModal} from '#component/modal';
import {PasswordInputField, TextInputField} from '#component/partial';
import {sprinkles} from '#style/sprinkles';
import {buildUrl} from '#util/url';
import {emailValidator, passwordValidator} from '#util/validator';

import {FieldValues, Props, SubmitHandler} from './SignInForm.types';

export const SignInForm = ({className, email = '', returnUrl, onSubmit, onError}: Props) => {
  const [hadErrors, setHadErrors] = useState(false);
  const {
    clearErrors,
    formState: {errors, isSubmitting},
    handleSubmit,
    register,
    setError,
  } = useForm<FieldValues>({
    defaultValues: {email, form: '', password: '', staySignedIn: true},
    mode: 'onBlur',
    resolver: yupResolver(object({email: emailValidator, password: passwordValidator})),
  });

  const handleSignIn: SubmitHandler = async ({email, password, staySignedIn}) => {
    try {
      await onSubmit({email, password, staySignedIn});
    } catch (err) {
      setError('form', {message: 'The email or password you entered is incorrect.'});

      if (onError) {
        onError(err as any as Error);
      }
    }
  };

  useEffect(() => {
    if (Object.keys(errors).length > 0 && !hadErrors) {
      setHadErrors(true);
    }
  }, [hadErrors, errors]);

  return (
    <Form
      className={className}
      clearErrors={clearErrors}
      errors={errors}
      onSubmit={handleSubmit(handleSignIn)}>
      <div className={sprinkles({flex: 'flex', direction: 'col', gap: 32})}>
        <TextInputField errors={errors} label="Email" {...register('email')} />

        <PasswordInputField errors={errors} label="Password" {...register('password')} />

        <div className={sprinkles({flex: 'flex', content: 'between', items: 'center'})}>
          <label className={sprinkles({flex: 'flex', gap: 8, items: 'center'})}>
            <Checkbox {...register('staySignedIn')} />
            <Text>Stay logged in</Text>
          </label>

          <Text color="doveGray" fontWeight="semiBold" variant="caption">
            <NextLink
              href={buildUrl({path: '/forgotpassword', queries: {return_to: returnUrl}})}
              passHref>
              <Link>Forgot your password?</Link>
            </NextLink>
          </Text>
        </div>
      </div>

      <div className={sprinkles({flex: 'flex', direction: 'col', gap: 20, content: 'center'})}>
        <Button disabled={isSubmitting} icon="ArrowRight" type="submit">
          Login
        </Button>

        <div
          className={sprinkles({
            borderRadius: 4,
            bg: 'springWood',
            flex: 'flex',
            dir: 'col',
            gap: 10,
            p: 20,
            items: 'center',
          })}>
          <Text
            as="p"
            className={sprinkles({flex: 'flex', gap: 8})}
            color="mineShaft"
            textAlign="center">
            Did you receive a test kit as a gift?
          </Text>

          <Text>
            <NextLink
              href={buildUrl({
                path: '/createaccount',
                queries: {from_gift_testkit: true, autoVerify: true},
              })}
              passHref>
              <Link variant="burgundy">Start a new account</Link>
            </NextLink>
          </Text>
        </div>

        <div
          className={sprinkles({
            borderRadius: 4,
            bg: 'springWood',
            flex: 'flex',
            dir: 'col',
            gap: 10,
            p: 20,
            items: 'center',
          })}>
          <Text
            as="p"
            className={sprinkles({flex: 'flex', gap: 8})}
            color="mineShaft"
            textAlign="center">
            Did you place an order with Tally Health™ and forget to create an account?
          </Text>

          <Text>
            <NextLink
              href={buildUrl({path: '/createaccount/request', queries: {return_to: returnUrl}})}
              passHref>
              <Link variant="burgundy">Complete your account setup</Link>
            </NextLink>
          </Text>

          {hadErrors && (
            <Text as="p" className={sprinkles({flex: 'flex', gap: 8})} color="dustyGray">
              Having issues?
              <ModalRoot>
                <Link as={ModalTrigger}>Contact Us</Link>
                <ContactUsModal />
              </ModalRoot>
            </Text>
          )}
        </div>
      </div>
    </Form>
  );
};
